import axios from 'axios';
import {
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
} from 'date-fns';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useState } from 'react';
import ReactTooltip from 'react-tooltip';
import InformationOutlineIcon from '../../assets/InformationOutlineIcon';
import { checkDomainNames } from '../../utils/checkDomainNames';
import { formatNumber } from '../../utils/format';

const calculateAddedPercentage = (numProcessed: number, numAdded: number) => {
  if (numProcessed === 0) return 0; // Avoid division by zero
  return (numAdded / numProcessed) * 100;
};

export async function fetchChartData(offsetPeriods = false, iso3?: string) {
  const startDate = offsetPeriods ? new Date(2023, 6, 1) : new Date(2024, 6, 1);
  const today = offsetPeriods ? new Date(2024, 6, 1) : new Date();

  const getDataFrom4D = async (start: string, end: string) => {
    const response = await axios({
      method: 'GET',
      url: `https://${checkDomainNames(
        'world_1_api.objsyshost.com'
      )}/api/v1/getSystemStatistics?beginDate=${start}&endDate=${end}${
        iso3 ? `&iso_3=${iso3}` : ''
      }`,
      'axios-retry': {
        retries: 2,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  };

  const intervals = {
    '1M': eachMonthOfInterval({
      start: startDate,
      end: today,
    }),
    '3M': eachQuarterOfInterval({
      start: startDate,
      end: today,
    }),
    '12M': eachYearOfInterval({
      start: startDate,
      end: today,
    }),
  };

  const labelFunctionMappings = {
    '1M': endOfMonth,
    '3M': endOfQuarter,
    '12M': endOfYear,
  };

  try {
    const fetchedIntervals = Object.fromEntries(
      await Promise.all(
        Object.entries(intervals).map(async ([label, dates]) => {
          const fetchedData = await Promise.all(
            dates.map((date) =>
              getDataFrom4D(
                format(date, 'MM-dd-yyyy'),
                format(
                  labelFunctionMappings[
                    label as keyof typeof labelFunctionMappings
                  ](date),
                  'MM-dd-yyyy'
                )
              )
            )
          );

          return [label, fetchedData];
        })
      )
    );

    return {
      '1M': fetchedIntervals['1M'].reduce(
        (
          acc: {
            categories: string[];
            series: { y: number; created: number }[];
          },
          intervalData: {
            result: {
              system_statistics: {
                num_humans_added: number;
                beginDate: string;
                num_humans_processed: number;
              };
            };
          }
        ) => {
          acc.categories.push(intervalData.result.system_statistics.beginDate);
          acc.series.push({
            y: intervalData.result.system_statistics.num_humans_processed,
            created: intervalData.result.system_statistics.num_humans_added,
          });

          return acc;
        },
        { categories: [], series: [] }
      ),
      '3M': fetchedIntervals['3M'].reduce(
        (
          acc: {
            categories: string[];
            series: { y: number; created: number }[];
          },
          intervalData: {
            result: {
              system_statistics: {
                num_humans_added: number;
                beginDate: string;
                num_humans_processed: number;
              };
            };
          }
        ) => {
          acc.categories.push(intervalData.result.system_statistics.beginDate);
          acc.series.push({
            y: intervalData.result.system_statistics.num_humans_processed,
            created: intervalData.result.system_statistics.num_humans_added,
          });

          return acc;
        },
        { categories: [], series: [] }
      ),
      '12M': fetchedIntervals['12M'].reduce(
        (
          acc: {
            categories: string[];
            series: { y: number; created: number }[];
          },
          intervalData: {
            result: {
              system_statistics: {
                num_humans_added: number;
                beginDate: string;
                num_humans_processed: number;
              };
            };
          }
        ) => {
          acc.categories.push(intervalData.result.system_statistics.beginDate);
          acc.series.push({
            y: intervalData.result.system_statistics.num_humans_processed,
            created: intervalData.result.system_statistics.num_humans_added,
          });

          return acc;
        },
        { categories: [], series: [] }
      ),
    };
  } catch (e) {
    console.error(e);
    return {};
  }
}

type RecordsStatsChartProps = {
  chartData: Record<
    string,
    { categories: string[]; series: { y: number; created: number }[] }
  >;
};

export default function RecordsStatsChart({
  chartData,
}: RecordsStatsChartProps) {
  const dateRanges = ['1M', '3M', '12M'];
  const [selectedDate, setSelectedDate] = useState('1M');

  const addedPercentage = calculateAddedPercentage(
    chartData?.[selectedDate]?.series
      ?.map(({ y }) => y)
      ?.reduce((a, b) => a + b) ?? 0,
    chartData?.[selectedDate]?.series
      ?.map(({ created }) => created)
      ?.reduce((a, b) => a + b) ?? 0
  );

  const addedDifference =
    calculateAddedPercentage(
      (chartData?.[selectedDate]?.series || [])?.at(-1)?.y ?? 0,
      (chartData?.[selectedDate]?.series || [])?.at(-1)?.created ?? 0
    ) -
    calculateAddedPercentage(
      (chartData?.[selectedDate]?.series || [])?.at(-2)?.y ?? 0,
      (chartData?.[selectedDate]?.series || [])?.at(-2)?.created ?? 0
    );

  const processed =
    chartData?.[selectedDate]?.series
      ?.map(({ y }) => y)
      ?.reduce((a: number, b: number) => a + b) || 0;

  const processedDifference =
    ((chartData?.[selectedDate]?.series || [])?.at(-1)?.y ?? 0) -
    ((chartData?.[selectedDate]?.series || [])?.at(-2)?.y ?? 0);

  return (
    <div className="mx-[20px] mt-6 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)] max-md:bg-[#FAFAFA] md:mx-[45px] tablet:mx-[80px]">
      <div className="flex flex-col gap-6 rounded-xl bg-white p-6">
        <p className="text-base font-semibold leading-7 text-zinc-950 md:text-lg">
          Records processed
        </p>
        <div className="flex flex-wrap justify-between gap-10 md:justify-start">
          <div className="flex flex-col gap-0.5">
            <div className="flex gap-1 text-xs font-normal leading-none text-zinc-500">
              <p>Processed</p>
              <InformationOutlineIcon fill="#a1a1aa" height={12} width={12} />
            </div>
            <div className="flex items-center gap-1">
              <p className="text-base font-semibold leading-loose text-zinc-950 md:text-xl">
                {formatNumber(processed)}
              </p>
              <p
                className={`text-xs font-medium leading-tight md:text-sm ${
                  processedDifference >= 0 ? 'text-[#109653]' : 'text-red-500'
                }`}
              >
                {processedDifference >= 0
                  ? '+' + formatNumber(processedDifference)
                  : formatNumber(processedDifference)}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-0.5">
            <div className="flex gap-1 text-xs font-normal leading-none text-zinc-500">
              <p>Created</p>
              <InformationOutlineIcon
                id="created-tooltip"
                tooltip="Percentage of the processed records leading to the creation of a new human"
                fill="#a1a1aa"
                height={12}
                width={12}
              />
              <ReactTooltip
                className="tooltip-opacity before:hidden"
                id="created-tooltip"
                type={'info'}
                effect={'solid'}
                place="top"
                backgroundColor={'#ECECEC'}
                textColor={'#454545'}
              />
            </div>
            <div className="flex items-center gap-1">
              <p className="text-base font-semibold leading-loose text-zinc-950 md:text-xl">
                {addedPercentage.toFixed(2)}%
              </p>
              <p
                className={`text-xs font-medium leading-tight md:text-sm ${
                  addedDifference >= 0 ? 'text-[#109653]' : 'text-red-500'
                }`}
              >
                {addedDifference >= 0
                  ? '+' + addedDifference.toFixed(2)
                  : addedDifference.toFixed(2)}
              </p>
            </div>
          </div>
          <div className="ml-auto flex h-7 w-full justify-between gap-0.5 align-bottom md:w-fit">
            {dateRanges.map((range, index) => (
              <button
                key={index}
                onClick={() => setSelectedDate(range)}
                className={`flex grow cursor-pointer items-center justify-center gap-6 rounded-md px-2 hover:bg-zinc-100 ${
                  range === selectedDate
                    ? 'bg-zinc-100 text-black'
                    : 'text-zinc-500'
                }`}
              >
                <p className="text-xs font-medium leading-none">{range}</p>
              </button>
            ))}
          </div>
        </div>
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            chart: {
              type: 'areaspline',
            },
            legend: { enabled: false },
            tooltip: {
              useHTML: true,
              backgroundColor: '#fff',
              borderRadius: 12,
              borderWidth: 0,
              shadow: {
                offsetX: 0,
                offsetY: 6,
                width: 16,
                opacity: 0.2,
                color: 'rgba(15, 25, 40, 0.20)',
              },
              style: {
                color: '#333',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
              },
              padding: 12,
              formatter: function (this: {
                y: number;
                created: number;
                category: string;
              }) {
                return `
                    <div class="flex flex-col gap-2 min-w-[130px]">
                        <p class="h-4 text-[#6f757e] text-xs font-normal leading-none">${
                          this.category
                        }</p>
                        <div class="flex flex-col gap-1">
                            <div class="flex justify-between">
                                <p class="text-zinc-950 text-xs font-normal leading-none">Processed</p>
                                <p class="ml-3 text-zinc-950 text-xs font-semibold leading-none">${formatNumber(
                                  this.y
                                )}</p>
                            </div>
                            <div class="flex justify-between">
                                <p class="text-zinc-950 text-xs font-normal leading-none">Created</p>
                                <p class="text-zinc-950 text-xs font-semibold leading-none">${formatNumber(
                                  calculateAddedPercentage(
                                    this.y,
                                    this.created
                                  ).toFixed(2)
                                )}%</p>
                            </div>
                        </div>
                    </div>`;
              },
            },
            title: {
              text: '',
            },
            yAxis: {
              title: '',
              gridLineColor: '#e4e4e7',
              gridLineDashStyle: 'Dash',
              labels: {
                style: {
                  color: '#71717a',
                  fontSize: '12px',
                  fontFamily: "'Montserrat', sans-serif",
                },
              },
            },
            plotOptions: {
              areaspline: {
                fillOpacity: 0.15,
                lineWidth: 2,
                pointPlacement: 'on',
              },
            },
            credits: {
              enabled: false,
            },
            xAxis: {
              type: 'category',
              lineColor: '#e4e4e7',
              crosshair: {
                width: 1,
                color: '#34A28A',
                dashStyle: 'Dash',
              },
              labels: {
                style: {
                  color: '#71717a',
                  fontSize: '12px',
                  fontFamily: "'Montserrat', sans-serif",
                },
              },
              categories: chartData?.[selectedDate]?.categories ?? [],
              min: 0,
              max: (chartData?.[selectedDate]?.categories?.length ?? 1) - 1,
              tickInterval: 1,
              tickmarkPlacement: 'on',
            },
            series: [
              {
                name: 'Processed',
                data: chartData?.[selectedDate]?.series ?? [],
                color: '#34A28A',
                fillColor: {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                  stops: [
                    [0, '#34A28A2F'],
                    [1, '#34A28A00'],
                  ],
                },

                marker: {
                  enabled: false,
                  states: {
                    hover: {
                      enabled: true,
                      radius: 4,
                      fillColor: '#34A28A',
                      lineColor: '#34A28A',
                      lineWidth: 2,
                      symbol: 'circle',
                    },
                  },
                  color: '#34A28A',
                },
              },
            ],
          }}
        />
      </div>
    </div>
  );
}
